import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  Skeleton,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  url as commonUrl,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Receivable, Util } from '@finley-technologies/finley-types';

import { LoanProductGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { UploadReceivablesCSVForm } from './UploadReceivablesCSVForm';
import { parseCompleteCurrentMasterLoanTapeSetForLoanProductIds } from './utils';
import { DeleteIconButton } from 'packages/common-frontend/components/IconButton';
import {
  GreenCircleCheck,
  RedCircleCross,
} from 'packages/common-frontend/components/icons';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useGetCreditFacilitiesQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useLatestAssignmentPipelineRunMetadataForLpGroupingContext } from 'packages/loanProduct/hooks/LatestAssignmentPipelineRunMetadataForLpGroupingProvider';
import { useLoanProductCreditFacilityGroupContext } from 'packages/loanProduct/hooks/LoanProductCreditFacilityGroupingProvider';
import { usePreviewGenerationStatus } from 'packages/loanProduct/hooks/useGetPreviewGenerationStatus';
import { AssignmentIncompleteCurrentMasterLoanTapeSetAlert } from 'packages/loanProduct/ui/AssignmentTemplate';
import { CheckLatestPipelineRunStatus } from 'packages/loanProduct/ui/v2/CheckLatestPipelineRunStatus';
import { useAuthToken } from 'packages/logged-out/hooks/useAuthManagement';
import { useGetOrganizationLoanProductsWithCurrentCreditFacilitiesQuery } from 'packages/organization/requests/organization.gql.generated';
import {
  FiFilePlus,
  FiMinusCircle,
  FiUpload,
  FiDownload,
  FiChevronLeft,
} from 'react-icons/fi';

type ReceivableError = { message: string; testReceivableId: string };

export type ReceivableUpload = {
  loanProductId: string;
  loanProductName: string;
  creditFacilityId: string;
  creditFacilityName: string;
  key: string;
  isValid: boolean | undefined; // undefined indicates either no file has been added or that file hasn't been validated yet
  fileError: string;
  errors: ReceivableError[];
  isLoading: boolean;
  file: File | null;
};

const initialUploadState = {
  isValid: undefined,
  errors: [],
  file: null,
  fileError: '',
};

// Using loanProductId-creditFacilityId as key
export type LoanProductCreditFacilityReceivableUploadMap = {
  [key: string]: ReceivableUpload;
};

const getStatusIcon = (
  isValid: boolean | undefined,
  isFileUploaded: boolean,
) => {
  if (!isFileUploaded) {
    return <FiMinusCircle />;
  }
  if (isFileUploaded && isValid === undefined) {
    return <FiFilePlus />;
  }
  if (isValid) {
    return <GreenCircleCheck />;
  }
  return <RedCircleCross />;
};

export const LoanProductCreditFacilityReceivableUpload = ({
  receivableUpload,
  setModalReceivableUpload,
  setReceivableUploadsMap,
  isGeneratingPreview,
}: {
  receivableUpload: ReceivableUpload;
  setModalReceivableUpload: (receivableUpload: ReceivableUpload) => void;
  setReceivableUploadsMap: React.Dispatch<
    React.SetStateAction<LoanProductCreditFacilityReceivableUploadMap>
  >;
  isGeneratingPreview: boolean;
}) => {
  const statusIcon = getStatusIcon(
    receivableUpload.isValid,
    !!receivableUpload.file,
  );

  const downloadCSVErrors = (filename: string, errors: ReceivableError[]) => {
    const csvRows = [];
    const headers = ['Error', 'Receivable Id'];
    csvRows.push(headers.join(','));
    errors.forEach((error) => {
      csvRows.push([error.message, error.testReceivableId].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const tag = document.createElement('a');
    tag.setAttribute('href', url);
    tag.setAttribute(
      'download',
      `${filename.substring(0, filename.length - 4)}-errors.csv`,
    );
    tag.click();
  };

  return (
    <Flex justify="flex-start" mt="18px" mb="18px" alignItems="center">
      <Flex
        justifyContent="center"
        flexWrap="wrap"
        ml="15px"
        mr="15px"
        width="20px"
      >
        {statusIcon}
      </Flex>
      <Text fontSize="lg" mr="40px" width="600px">
        {receivableUpload.loanProductName} &{' '}
        {receivableUpload.creditFacilityName}
      </Text>
      {receivableUpload.isValid === false ? (
        <Flex direction="column" width="400px" mr="40px">
          {receivableUpload.fileError ? (
            <Text fontSize="lg" color="red">
              {receivableUpload.fileError}
            </Text>
          ) : (
            <>
              <Text fontSize="lg" mb="10px" color="red">
                Some of the proposed receivables are invalid. Fix errors and try
                again.
              </Text>
              <Button
                size="sm"
                aria-label="download csv errors"
                rightIcon={<FiDownload />}
                width="fit-content"
                onClick={() => {
                  if (receivableUpload.file) {
                    downloadCSVErrors(
                      receivableUpload.file.name,
                      receivableUpload.errors,
                    );
                  }
                }}
              >
                Download Errors
              </Button>
            </>
          )}
        </Flex>
      ) : (
        <Text fontSize="lg" mr="40px" width="400px">
          {receivableUpload.file
            ? receivableUpload.file.name
            : 'No File uploaded'}
        </Text>
      )}

      <Button
        colorScheme={receivableUpload.isValid === false ? 'red' : 'green'}
        aria-label="upload receivables by csv"
        isLoading={receivableUpload.isLoading}
        onClick={() => {
          setModalReceivableUpload(receivableUpload);
        }}
        disabled={isGeneratingPreview}
        variant="outline"
        rightIcon={<FiUpload />}
      >
        {receivableUpload.file ? 'Re-upload ' : 'Upload '}File
      </Button>
      <Flex ml="10px" mr="10px">
        <DeleteIconButton
          ariaLabel="remove receivables csv"
          label="Remove Receivables CSV"
          onClick={() => {
            setReceivableUploadsMap((receivableUploadsMap) => {
              return {
                ...receivableUploadsMap,
                [receivableUpload.key]: {
                  ...receivableUploadsMap[receivableUpload.key],
                  isLoading: false,
                  ...initialUploadState,
                },
              };
            });
          }}
          fontSize="2xl"
          colorScheme="red"
          isDisabled={!receivableUpload.file || isGeneratingPreview}
        />
      </Flex>
    </Flex>
  );
};

export const AssignmentEditByCSVBody = ({
  organizationId,
  loanProductsForOrganization,
  masterLoanTapeIds,
  fileVersion,
  reportingDate,
}: {
  organizationId: string;
  loanProductsForOrganization: LoanProductGQLType[];
  masterLoanTapeIds: string[];
  fileVersion: number;
  reportingDate: Util.FullDateStr;
}) => {
  const [
    receivableUploadsMap,
    setReceivableUploadsMap,
  ] = useState<LoanProductCreditFacilityReceivableUploadMap>({});

  const [
    showGeneratePreviewAlert,
    setShowGeneratePreviewAlert,
  ] = useState<boolean>(false);

  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);

  const [isGeneratingPreview, setIsGeneratingPreview] = useState<boolean>(
    false,
  );

  const [
    modalReceivableUpload,
    setModalReceivableUpload,
  ] = useState<ReceivableUpload>();
  const onClose = () => {
    setModalReceivableUpload(undefined);
  };

  const [authToken] = useAuthToken();
  const toast = useToast();
  const history = useHistory();

  useEffect(() => {
    setShowGeneratePreviewAlert(false);
  }, [receivableUploadsMap]);

  const generatePreview = async (): Promise<void> => {
    const formData = new FormData();
    const uploadData: {
      loanProductId: string;
      creditFacilityId: string;
    }[] = [];

    Object.values(receivableUploadsMap).forEach((upload) => {
      if (upload.isValid === undefined && upload.file) {
        // isValid === undefined indicates either no file has been added or that file hasn't been validated yet
        uploadData.push({
          loanProductId: upload.loanProductId,
          creditFacilityId: upload.creditFacilityId,
        });
        formData.append(
          `${upload.loanProductId}${upload.creditFacilityId}file`,
          upload.file,
        );
      }
    });

    const url = commonUrl.getBorrowerExpressUrl(
      commonUrl.borrowerExpressPaths.assignByCSVPreviewGenAndCSVUpload,
    );
    formData.append('organizationId', organizationId);
    formData.append('uploadInfo', JSON.stringify(uploadData));
    formData.append('masterLoanTapeIds', JSON.stringify(masterLoanTapeIds));
    formData.append('fileVersion', String(fileVersion));
    formData.append('reportingDate', reportingDate);

    try {
      const { data } = await axios.post(url, formData, {
        timeout: 300000, // 5 minute timeout
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${authToken}`,
        },
      });

      const newReceivableUploadsMap = { ...receivableUploadsMap };
      data.uploadResults.forEach(
        (upload: {
          key: string;
          isValid: boolean;
          errors: ReceivableError[];
        }) => {
          const updatedReceivableUpload = {
            ...receivableUploadsMap[upload.key],
            isValid: upload.isValid,
            errors: upload.errors,
          };
          newReceivableUploadsMap[upload.key] = updatedReceivableUpload;
        },
      );
      setReceivableUploadsMap(newReceivableUploadsMap);
      if (data.areCSVsValid) {
        setShowSuccessAlert(true);
        setTimeout(() => {
          setShowSuccessAlert(false);
          history.push(`/assignments/summary/${data.assignmentPreviewJobId}`);
        }, 4000);
      }
    } catch (err: any) {
      Sentry.captureException(JSON.stringify(err, null, 2));
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error generating preview and saving receivables file. Please try again, and contact your Finley representative if this persists.',
      });
    }
  };

  const {
    data: creditFacilitiesForBorrowerData,
    loading: creditFacilitiesForBorrowerLoading,
  } = useGetCreditFacilitiesQuery({
    variables: {
      organizationId,
    },
    onCompleted(data) {
      const creditFacilityNameMapping: { [id: string]: string } = {};
      data.creditFacilities.forEach((creditFacility) => {
        creditFacilityNameMapping[creditFacility.id] = creditFacility.slug;
      });

      const receivableUploadMap: LoanProductCreditFacilityReceivableUploadMap = {};
      loanProductsForOrganization.forEach((loanProduct) => {
        loanProduct.currentCreditFacilities?.forEach((creditFacilityId) => {
          receivableUploadMap[loanProduct.id + creditFacilityId] = {
            loanProductId: loanProduct.id,
            loanProductName: loanProduct.name,
            creditFacilityId,
            creditFacilityName: commonUtils.creditFacilitySlugToName(
              creditFacilityNameMapping[creditFacilityId],
            ),
            key: loanProduct.id + creditFacilityId,
            isLoading: false,
            ...initialUploadState,
          };
        });
      });
      setReceivableUploadsMap(receivableUploadMap);
    },
    fetchPolicy: 'network-only',
  });

  if (
    !creditFacilitiesForBorrowerData ||
    !creditFacilitiesForBorrowerData.creditFacilities ||
    creditFacilitiesForBorrowerLoading
  ) {
    return <Skeleton />;
  }

  // Perform basic file structure validation on each file addition, checking that the file is single column.
  // Then all receivable validation is performed when user clicks Preview Proposed Owners.
  // If some files are invalid, those errors are displayed. If all files are valid, then preview is generated.

  const validateFileStructure = async (
    file: File,
    receivableUpload: ReceivableUpload | undefined,
  ): Promise<void> => {
    if (!receivableUpload) return;
    // Reset error state and display loading icon
    setReceivableUploadsMap({
      ...receivableUploadsMap,
      [receivableUpload.key]: {
        ...receivableUploadsMap[receivableUpload.key],
        isLoading: true,
        ...initialUploadState,
      },
    });

    const url = commonUrl.getBorrowerExpressUrl(
      commonUrl.borrowerExpressPaths.assignByCSVValidation,
    );
    const formData = new FormData();
    formData.append('loanProductId', receivableUpload.loanProductId);
    formData.append('creditFacilityId', receivableUpload.creditFacilityId);
    formData.append('receivables', file);

    try {
      const { data } = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${authToken}`,
        },
      });

      const fileError = data.isValid ? '' : data.fileErrors[0];

      const updatedReceivableUpload = {
        ...receivableUploadsMap[receivableUpload.key],
        isValid: data.isValid === false ? false : undefined,
        isLoading: false,
        fileError,
        file,
      };
      setReceivableUploadsMap({
        ...receivableUploadsMap,
        [updatedReceivableUpload.key]: updatedReceivableUpload,
      });
    } catch (err: any) {
      Sentry.captureException(JSON.stringify(err, null, 2));
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error processing file. Please try again, and contact your Finley representative if this persists.',
      });
      const updatedReceivableUpload = {
        ...receivableUploadsMap[receivableUpload.key],
        // Stop the spinner.
        isLoading: false,
      };
      setReceivableUploadsMap({
        ...receivableUploadsMap,
        [updatedReceivableUpload.key]: updatedReceivableUpload,
      });
    } finally {
      setModalReceivableUpload(undefined);
    }
  };

  return (
    <>
      {_.map(receivableUploadsMap, (receivableUpload) => {
        return (
          <>
            <LoanProductCreditFacilityReceivableUpload
              key={receivableUpload.key}
              receivableUpload={receivableUpload}
              setModalReceivableUpload={setModalReceivableUpload}
              setReceivableUploadsMap={setReceivableUploadsMap}
              isGeneratingPreview={isGeneratingPreview}
            />
            <hr />
          </>
        );
      })}
      <Flex direction="column" mt="150px">
        {showGeneratePreviewAlert && (
          <Alert status="error" mb="10px">
            <AlertIcon />
            <AlertDescription>
              Please upload files or fix errors to generate a new preview.
            </AlertDescription>
          </Alert>
        )}
        {isGeneratingPreview && (
          <Alert status="warning" mb="10px">
            <AlertIcon />
            <AlertDescription>Verifying file uploads.</AlertDescription>
          </Alert>
        )}
        {showSuccessAlert && (
          <Alert status="success" mb="10px">
            <AlertIcon />
            <AlertDescription>
              Successfully verified and uploaded files. You will be redirected
              to the summary page shortly.
            </AlertDescription>
          </Alert>
        )}
        <hr />
        <Flex justifyContent="space-between" mt="10px">
          <Button
            leftIcon={<FiChevronLeft />}
            variant="ghost"
            onClick={() => {
              history.push('/assignments/summary');
            }}
          >
            Back
          </Button>
          <Button
            colorScheme="aurora"
            onClick={async () => {
              try {
                const validUploadExists = Object.values(
                  receivableUploadsMap,
                ).find((upload) => upload.file && upload.isValid === undefined);
                if (!validUploadExists) {
                  setShowGeneratePreviewAlert(true);
                } else {
                  setIsGeneratingPreview(true);
                  await generatePreview();
                  setIsGeneratingPreview(false);
                }
              } catch (err: any) {
                if (!toast.isActive(errorToastId)) {
                  errorToastConfig.title = `Error previewing assignment`;
                  toast(errorToastConfig);
                }
                throw err;
              }
            }}
            isLoading={isGeneratingPreview}
            disabled={isGeneratingPreview}
          >
            Preview Proposed Owners
          </Button>
        </Flex>
      </Flex>
      <Modal
        isOpen={Boolean(modalReceivableUpload)}
        onClose={onClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent padding="20px" width="auto" minW="550px">
          <ModalHeader>
            {modalReceivableUpload &&
              `${modalReceivableUpload.loanProductName} & ${modalReceivableUpload.creditFacilityName}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UploadReceivablesCSVForm
              onClose={onClose}
              handleFile={(file) =>
                validateFileStructure(file, modalReceivableUpload)
              }
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export const AssignmentEditByCSVContainer = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const { lpToCfEntityGrouping } = useLoanProductCreditFacilityGroupContext();
  const {
    metadata,
  } = useLatestAssignmentPipelineRunMetadataForLpGroupingContext();

  const groupingLoanProductIds =
    lpToCfEntityGrouping?.loanProducts.map(({ id }) => id) ?? [];

  const {
    isCompleted,
    latestJob,
    StatusAlert: PreviewStatusAlert,
  } = usePreviewGenerationStatus({
    loanProductIds: groupingLoanProductIds,
    organizationId,
    reportingDate: metadata?.reportingDate,
    fileVersion: metadata?.fileVersion,
  });

  const {
    data: organizationLoanProductsData,
    loading: organizationLoanProductsLoading,
  } = useGetOrganizationLoanProductsWithCurrentCreditFacilitiesQuery({
    variables: {
      organizationId,
    },
  });

  if (
    !organizationLoanProductsData ||
    !organizationLoanProductsData.organization ||
    organizationLoanProductsLoading
  ) {
    return <Skeleton />;
  }

  const loanProductsForOrganization = organizationLoanProductsData.organization.loanProducts.filter(
    (loanProduct) => groupingLoanProductIds.includes(loanProduct.id),
  );

  const completeCurrentMasterLoanTapeSetForLoanProductIds = parseCompleteCurrentMasterLoanTapeSetForLoanProductIds(
    loanProductsForOrganization,
  );

  if (!completeCurrentMasterLoanTapeSetForLoanProductIds) {
    return <AssignmentIncompleteCurrentMasterLoanTapeSetAlert />;
  }

  const {
    fileVersion,
    reportingDate,
    masterLoanTapeIds,
  } = completeCurrentMasterLoanTapeSetForLoanProductIds;

  return (
    <Box>
      <CheckLatestPipelineRunStatus>
        {({ StatusAlert }) => {
          if (metadata === undefined || isCompleted === null) {
            return <Skeleton />;
          }
          if (
            !metadata ||
            metadata.status !== 'success' ||
            (metadata.status === 'success' && !isCompleted)
          ) {
            let status: Receivable.Assignment.Pipeline.Status;
            if (!metadata) {
              status = 'noPipelineRun';
            } else if (!isCompleted) {
              status = 'inProgress';
            } else {
              status = metadata.status;
            }
            return (
              <Flex>
                <StatusAlert status={status} />
                <Skeleton w="100%" h="50%" />
              </Flex>
            );
          }
          return (
            <>
              {latestJob &&
                latestJob.status === 'error' &&
                latestJob.assignmentMethod === 'csv' && (
                  <Box mb="32px">
                    <PreviewStatusAlert
                      text="An error occurred during the latest assignment process. Review your
            .CSV file(s) and try again."
                    />
                  </Box>
                )}
              <AssignmentEditByCSVBody
                organizationId={organizationId}
                loanProductsForOrganization={loanProductsForOrganization}
                masterLoanTapeIds={masterLoanTapeIds}
                fileVersion={fileVersion}
                reportingDate={reportingDate}
              />
            </>
          );
        }}
      </CheckLatestPipelineRunStatus>
    </Box>
  );
};
